<template>
  <div class="notifications-page">
    <div class="bg-grid"></div>
    <div class="bg-glow bg-glow-1"></div>

    <div class="notifications-container">
      <div class="page-header">
        <h1>Notifications</h1>
        <p class="subtitle">Configure how you want to be notified about site changes</p>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner-large"></div>
        <p>Loading preferences...</p>
      </div>

      <Transition name="fade">
        <div v-if="successMessage" class="message success-message">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <span>{{ successMessage }}</span>
        </div>
      </Transition>

      <Transition name="fade">
        <div v-if="error" class="message error-message">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          <span>{{ error }}</span>
        </div>
      </Transition>

      <div v-if="!loading" class="content-card">
        <div class="card-header">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          <div>
            <h2>AI Change Detection Alerts</h2>
            <p>Get notified when our AI detects and classifies changes on your monitored sites</p>
          </div>
        </div>

        <div class="preferences-list">
          <!-- Safe changes -->
          <div class="preference-row">
            <div class="preference-info">
              <div class="preference-label">
                <span class="severity-dot safe"></span>
                <span class="severity-text">Safe Changes</span>
              </div>
              <p class="preference-description">Changes detected and classified as safe by AI</p>
            </div>
            <div class="toggle-group">
              <div class="toggle-item">
                <span class="toggle-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  Email
                </span>
                <button
                  class="toggle-switch"
                  :class="{ active: preferences.safeEmail }"
                  :disabled="saving"
                  @click="toggle('safeEmail')"
                  role="switch"
                  :aria-checked="preferences.safeEmail"
                >
                  <span class="toggle-knob"></span>
                </button>
              </div>
              <div class="toggle-item">
                <span class="toggle-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                  Push
                </span>
                <button
                  class="toggle-switch"
                  :class="{ active: preferences.safePush }"
                  :disabled="saving"
                  @click="toggle('safePush')"
                  role="switch"
                  :aria-checked="preferences.safePush"
                >
                  <span class="toggle-knob"></span>
                </button>
              </div>
            </div>
          </div>

          <!-- Suspicious changes -->
          <div class="preference-row">
            <div class="preference-info">
              <div class="preference-label">
                <span class="severity-dot suspicious"></span>
                <span class="severity-text">Suspicious Changes</span>
              </div>
              <p class="preference-description">Changes detected and flagged as suspicious by AI</p>
            </div>
            <div class="toggle-group">
              <div class="toggle-item">
                <span class="toggle-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  Email
                </span>
                <button
                  class="toggle-switch"
                  :class="{ active: preferences.suspiciousEmail }"
                  :disabled="saving"
                  @click="toggle('suspiciousEmail')"
                  role="switch"
                  :aria-checked="preferences.suspiciousEmail"
                >
                  <span class="toggle-knob"></span>
                </button>
              </div>
              <div class="toggle-item">
                <span class="toggle-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                  Push
                </span>
                <button
                  class="toggle-switch"
                  :class="{ active: preferences.suspiciousPush }"
                  :disabled="saving"
                  @click="toggle('suspiciousPush')"
                  role="switch"
                  :aria-checked="preferences.suspiciousPush"
                >
                  <span class="toggle-knob"></span>
                </button>
              </div>
            </div>
          </div>

          <!-- Malicious changes -->
          <div class="preference-row">
            <div class="preference-info">
              <div class="preference-label">
                <span class="severity-dot malicious"></span>
                <span class="severity-text">Malicious Changes</span>
              </div>
              <p class="preference-description">Changes detected and marked as malicious by AI</p>
            </div>
            <div class="toggle-group">
              <div class="toggle-item">
                <span class="toggle-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  Email
                </span>
                <button
                  class="toggle-switch"
                  :class="{ active: preferences.maliciousEmail }"
                  :disabled="saving"
                  @click="toggle('maliciousEmail')"
                  role="switch"
                  :aria-checked="preferences.maliciousEmail"
                >
                  <span class="toggle-knob"></span>
                </button>
              </div>
              <div class="toggle-item">
                <span class="toggle-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                  Push
                </span>
                <button
                  class="toggle-switch"
                  :class="{ active: preferences.maliciousPush }"
                  :disabled="saving"
                  @click="toggle('maliciousPush')"
                  role="switch"
                  :aria-checked="preferences.maliciousPush"
                >
                  <span class="toggle-knob"></span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="card-footer">
          <button class="btn btn-primary" :disabled="saving || !hasChanges" @click="savePreferences">
            <span v-if="saving" class="spinner"></span>
            <template v-else>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Save Preferences
            </template>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { api } from '../utils/api'

export default {
  name: 'NotificationsView',
  inject: ['env'],
  data() {
    return {
      loading: true,
      saving: false,
      error: null,
      successMessage: null,
      preferences: {
        safeEmail: false,
        safePush: false,
        suspiciousEmail: true,
        suspiciousPush: true,
        maliciousEmail: true,
        maliciousPush: true
      },
      savedPreferences: null
    }
  },
  computed: {
    hasChanges() {
      if (!this.savedPreferences) return true
      return JSON.stringify(this.preferences) !== JSON.stringify(this.savedPreferences)
    }
  },
  async created() {
    await this.fetchPreferences()
  },
  methods: {
    toggle(key) {
      this.preferences[key] = !this.preferences[key]
      this.clearMessages()
    },
    clearMessages() {
      this.error = null
      this.successMessage = null
    },
    async fetchPreferences() {
      try {
        const data = await api('/users/notification-preferences', {}, this.$router)
        if (data) {
          this.preferences = {
            safeEmail: data.safeEmail ?? false,
            safePush: data.safePush ?? false,
            suspiciousEmail: data.suspiciousEmail ?? true,
            suspiciousPush: data.suspiciousPush ?? true,
            maliciousEmail: data.maliciousEmail ?? true,
            maliciousPush: data.maliciousPush ?? true
          }
          this.savedPreferences = { ...this.preferences }
        }
      } catch (error) {
        if (error.status !== 404) {
          this.error = error.code || 'Failed to load notification preferences'
        }
        this.savedPreferences = { ...this.preferences }
      } finally {
        this.loading = false
      }
    },
    async savePreferences() {
      this.saving = true
      this.clearMessages()

      try {
        await api('/users/notification-preferences', {
          method: 'PUT',
          body: JSON.stringify(this.preferences)
        }, this.$router)

        this.savedPreferences = { ...this.preferences }
        this.successMessage = 'Notification preferences saved successfully'
        setTimeout(() => { this.successMessage = null }, 4000)
      } catch (error) {
        this.error = error.code || 'Failed to save notification preferences'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.notifications-page {
  min-height: calc(100vh - 40px);
  position: relative;
  padding: 40px 20px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image: 
    linear-gradient(rgba(60, 98, 230, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(60, 98, 230, 0.02) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.25;
  pointer-events: none;
  z-index: 0;
}

.bg-glow-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(60, 98, 230, 0.25) 0%, transparent 70%);
  top: -100px;
  right: 10%;
}

.notifications-container {
  position: relative;
  z-index: 1;
  max-width: 640px;
  margin: 0 auto;
  animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.subtitle {
  color: var(--text-secondary);
  margin: 0;
  font-size: 0.95rem;
}

/* Loading */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-secondary);
}

.loading-state p {
  margin-top: 16px;
  font-size: 1rem;
}

.spinner-large {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(60, 98, 230, 0.2);
  border-top-color: var(--accent-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Messages */
.message {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  font-size: 0.9rem;
}

.message svg {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 2px;
}

.success-message {
  background: rgba(94, 194, 105, 0.1);
  border: 1px solid rgba(94, 194, 105, 0.2);
  color: var(--success-color);
}

.error-message {
  background: rgba(221, 82, 76, 0.1);
  border: 1px solid rgba(221, 82, 76, 0.2);
  color: var(--error-color);
}

/* Card */
.content-card {
  background: rgba(24, 24, 25, 0.7);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 28px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(60, 98, 230, 0.03);
}

.card-header > svg {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  color: var(--accent-color);
  margin-top: 2px;
}

.card-header h2 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.card-header p {
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin: 0;
}

/* Preferences */
.preferences-list {
  padding: 8px 0;
}

.preference-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 20px 28px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  transition: background-color 0.15s ease;
}

.preference-row:last-child {
  border-bottom: none;
}

.preference-row:hover {
  background: rgba(255, 255, 255, 0.02);
}

.preference-info {
  flex: 1;
  min-width: 0;
}

.preference-label {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}

.severity-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.severity-dot.safe {
  background: var(--success-color, #5ec269);
  box-shadow: 0 0 8px rgba(94, 194, 105, 0.4);
}

.severity-dot.suspicious {
  background: var(--warning-color, #e39d3b);
  box-shadow: 0 0 8px rgba(227, 157, 59, 0.4);
}

.severity-dot.malicious {
  background: var(--error-color, #dd524c);
  box-shadow: 0 0 8px rgba(221, 82, 76, 0.4);
}

.severity-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
}

.preference-description {
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin: 0;
  padding-left: 20px;
}

/* Toggle group */
.toggle-group {
  display: flex;
  gap: 20px;
  flex-shrink: 0;
}

.toggle-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: var(--text-secondary);
  font-weight: 500;
}

.toggle-label svg {
  width: 13px;
  height: 13px;
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  border: none;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: background-color 0.25s ease;
  padding: 0;
}

.toggle-switch:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.15);
}

.toggle-switch.active {
  background: var(--accent-color, #3c62e6);
}

.toggle-switch.active:hover:not(:disabled) {
  background: #4a70f0;
}

.toggle-switch:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  transition: transform 0.25s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.toggle-switch.active .toggle-knob {
  transform: translateX(20px);
}

/* Footer */
.card-footer {
  padding: 20px 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  justify-content: flex-end;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  font-family: inherit;
  transition: all 0.2s ease;
}

.btn svg {
  width: 18px;
  height: 18px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, var(--accent-color) 0%, #2a4bc4 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(60, 98, 230, 0.3);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(60, 98, 230, 0.4);
}

/* Spinner */
.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 640px) {
  .notifications-page {
    padding: 20px 16px;
  }

  .preference-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 16px 20px;
  }

  .toggle-group {
    width: 100%;
    justify-content: flex-start;
    gap: 24px;
    padding-left: 20px;
  }

  .card-header {
    padding: 20px;
  }

  .card-footer {
    padding: 16px 20px;
  }

  .btn-primary {
    width: 100%;
  }
}
</style>
